import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import { useCreateAd, useGetAdById, useUpdateAd } from "../../queries/advertisement";
import { useGetBrands } from "../../queries/brand";
import { useGetActiveOrganiser } from "../../queries/user";
import { toast, ToastContainer } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';
import MediaUpload from "../../components/Forms/MediaUpload";
import { ActionButton } from '../../components/Forms/ActionButton';
import ActiveStatus from '../../components/Common/ActiveStatus';
import NewDropdown from '../../components/Forms/NewDropdown';

const CreateAd = () => {
    const navigate = useNavigate();
    const { state } = useLocation();
    const adId = state?.adId || null;

    const { data: adData, isLoading: isAdDataLoading } = useGetAdById(adId);
    const { data: organiserList = [] } = useGetActiveOrganiser();

    const {
        register,
        handleSubmit,
        formState: { errors, isSubmitting },
        reset,
        watch,
        setValue,
        trigger,
    } = useForm({
        defaultValues: {
            name: '',
            type: 'Client',
            adText: '',
            buttonText: '',
            link: '',
            brandId: '',
            clientId: '',
            renderFormat: 'image',
        }
    });

    const [active, setActive] = useState(true);
    const [adImage, setAdImage] = useState(null);
    const [image, setImage] = useState(null);
    const [mediaClear, setMediaClear] = useState(false);

    // Fetch data for select options
    const { data: brandsList = [] } = useGetBrands();

    const { mutateAsync: createAd, isPending: isCreating } = useCreateAd();
    const { mutateAsync: updateAd, isPending: isUpdating } = useUpdateAd(adId);

    const watchedType = watch("type");
    const watchedBrandId = watch("brandId");
    const watchedClientId = watch("clientId");
    const watchedRenderFormat = watch("renderFormat");

    const onSubmit = async (data) => {
        try {
            const finalData = {
                active: active,
                name: data.name,
                type: data.type,
                adText: data.adText || '',
                buttonText: data.buttonText || '',
                link: data.link || '',
                brandId: data.brandId || null,
                clientId: data.clientId || null,
                renderFormat: data.renderFormat || 'image',
            }

            if (adImage) {
                finalData.adImage = adImage;
            }
            if (image) {
                finalData.image = image;
            }

            console.log('Processed ad data:', finalData);

            if (adId) {
                finalData.id = adId;
                await updateAd(finalData, {
                    onSuccess: (res) => {
                        if (res.error) {
                            console.log(res);
                            toast.error(res.error);
                        } else {
                            toast.success('Advertisement updated successfully!');
                            setTimeout(() => {
                                navigate(-1);
                            }, 2000);
                        }
                    },
                    onError: (error) => {
                        console.error('Error updating advertisement:', error);
                        toast.error('Failed to update advertisement. Please try again.');
                    }
                });
            } else {
                await createAd(finalData, {
                    onSuccess: (res) => {
                        if (res.error) {
                            console.log(res);
                            toast.error(res.error);
                        } else {
                            toast.success('Advertisement created successfully!');
                            reset();
                            setAdImage(null);
                            setImage(null);
                            setTimeout(() => {
                                navigate(-1);
                            }, 2000);
                        }
                    },
                    onError: (error) => {
                        console.error('Error creating advertisement:', error);
                        toast.error('Failed to create advertisement. Please try again.');
                    }
                });
            }
        } catch (error) {
            console.error('Error saving advertisement:', error);
            toast.error(`Error saving advertisement: ${error.message || 'Unknown error'}`);
        }
    };

    useEffect(() => {
        setMediaClear(false);
    }, [adImage, image]);

    useEffect(() => {
        setTimeout(() => {
            if (adData && adData.data) {
                const ad = adData.data;
                setActive(ad.active !== undefined ? ad.active : true);
                setValue("name", ad.name || '');
                setValue("type", ad.type || 'Client');
                setValue("adText", ad.adText || '');
                setValue("buttonText", ad.buttonText || '');
                setValue("link", ad.link || '');
                setValue("brandId", ad.brandId?._id || '');
                setValue("clientId", ad.clientId?._id || '');
                setValue("renderFormat", ad.renderFormat || 'image');
                setAdImage(ad.adImage || null);
                setImage(ad.image || null);
            }
        }, 200);
    }, [adData, setValue]);

    if (isAdDataLoading || !brandsList || !organiserList) {
        return (
            <div className="flex items-center justify-center h-screen">
                <div className="text-black">Loading...</div>
            </div>
        );
    }

    const clientOptions = watchedType === 'Client' ? organiserList : [];

    return (
        <div className="flex flex-col h-screen w-full font-semibold text-base overflow-visible">
            <div className='flex items-center justify-end'>
                <ActiveStatus
                    active={active}
                    setActive={setActive}
                    isCreateForm={true}
                />
            </div>
            <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col w-full font-semibold overflow-visible">
                <section className="flex flex-col justify-center px-5 pt-4 py-9 gap-3 w-full bg-white rounded-md shadow-[0px_4px_25px_rgba(94,92,154,0.1)] overflow-visible">
                    
                    <div className="flex gap-6">
                        {/* Main Form Fields */}
                        <div className="flex-1">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

                                {/* Advertisement Name */}
                                <div className="flex flex-col shrink-0 basis-0 w-full">
                                    <label className="gap-1 self-stretch text-[14px] w-full text-gray-900 font-[600] capitalize">
                                        <span className="text-red text-xs inline">*</span> Advertisement Name
                                    </label>
                                    <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                        <input
                                            type="text"
                                            placeholder="Enter Advertisement Name"
                                            {...register("name", {
                                                required: "Advertisement Name is required",
                                                minLength: { value: 2, message: "Advertisement name must be at least 2 characters" }
                                            })}
                                            className={`flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-2.5 rounded-md text-base ${errors.name ? "border-red-500" : ""}`}
                                        />
                                    </div>
                                    {errors.name && <p className="text-red text-sm">{errors.name.message}</p>}
                                </div>

                                {/* Type */}
                                <div className='flex flex-col gap-1 basis-1/2'>
                                    <label htmlFor="type" className="gap-1 self-stretch w-full text-gray-900 text-[14px] font-[600]">
                                        <span className="text-red text-xs inline">* </span>
                                        Type
                                    </label>
                                    <NewDropdown
                                        name={"type"}
                                        dataList={[
                                            { _id: 'Client', name: 'Client' },
                                            { _id: 'CC', name: 'CC' },
                                            { _id: 'Filler', name: 'Filler' }
                                        ]}
                                        value={watchedType}
                                        placeholder={'Select Type'}
                                        onChange={(e) => {
                                            setValue("type", e.value);
                                            if (e.value) {
                                                trigger("type");
                                            }
                                        }}
                                    />
                                    {errors.type && <p className="text-red text-sm mt-1">{errors.type.message}</p>}
                                </div>

                                {/* Render Format - Full Width */}
                                <div className="col-span-2 flex flex-col gap-3">
                                    <label className="gap-1 self-stretch text-[14px] w-full text-gray-900 font-[600]">
                                        <span className="text-red text-xs inline">* </span>
                                        Ad Render Format
                                    </label>
                                    <div className="flex gap-6">
                                        {/* Image Format Option */}
                                        <div 
                                            className={`flex flex-col items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
                                                watchedRenderFormat === 'image' 
                                                    ? 'border-primary bg-primary/10' 
                                                    : 'border-navBtn hover:border-secondary'
                                            }`}
                                            onClick={() => {
                                                setValue("renderFormat", "image");
                                                trigger("renderFormat");
                                            }}
                                        >
                                            <div className="mb-3 w-40 h-32 flex items-center justify-center bg-gray-50 rounded border border-navBtn hover:border-secondary overflow-hidden">
                                                <img 
                                                    src="/ad-format-image.jpeg"
                                                    alt="Image Format"
                                                    className="max-w-full max-h-full object-contain"
                                                />
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <input
                                                    type="radio"
                                                    id="renderFormat-image"
                                                    {...register("renderFormat", { required: "Please select a render format" })}
                                                    value="image"
                                                    className="w-4 h-4 text-primary"
                                                />
                                                <label htmlFor="renderFormat-image" className="text-sm font-medium cursor-pointer">
                                                    Image Format
                                                </label>
                                            </div>
                                        </div>

                                        {/* Full Format Option */}
                                        <div 
                                            className={`flex flex-col items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
                                                watchedRenderFormat === 'full' 
                                                    ? 'border-primary bg-primary/10' 
                                                    : 'border-navBtn hover:border-secondary'
                                            }`}
                                            onClick={() => {
                                                setValue("renderFormat", "full");
                                                trigger("renderFormat");
                                            }}
                                        >
                                            <div className="mb-3 w-40 h-32 flex items-center justify-center bg-gray-50 rounded border border-navBtn hover:border-secondary overflow-hidden">
                                                <img 
                                                    src="/ad-format-full.jpeg"
                                                    alt="Full Format"
                                                    className="max-w-full max-h-full object-contain"
                                                />
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <input
                                                    type="radio"
                                                    id="renderFormat-full"
                                                    {...register("renderFormat", { required: "Please select a render format" })}
                                                    value="full"
                                                    className="w-4 h-4 text-primary"
                                                />
                                                <label htmlFor="renderFormat-full" className="text-sm font-medium cursor-pointer">
                                                    Full Format
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                    {errors.renderFormat && <p className="text-red text-sm mt-1">{errors.renderFormat.message}</p>}
                                </div>

                                {/* Brand */}
                                <div className='flex flex-col gap-1 basis-1/2'>
                                    <label htmlFor="brandId" className="gap-1 self-stretch w-full text-gray-900 text-[14px] font-[600]">
                                        Brand
                                    </label>
                                    <NewDropdown
                                        name={"brandId"}
                                        dataList={brandsList?.map(brand => ({ _id: brand._id, name: brand.brandName })) || []}
                                        value={watchedBrandId}
                                        placeholder={'Select Brand'}
                                        onChange={(e) => {
                                            setValue("brandId", e.value);
                                            if (e.value) {
                                                trigger("brandId");
                                            }
                                        }}
                                    />
                                </div>

                                {/* Client - Only show for Client type */}
                                {watchedType === 'Client' && (
                                    <div className='flex flex-col gap-1 basis-1/2'>
                                        <label htmlFor="clientId" className="gap-1 self-stretch w-full text-gray-900 text-[14px] font-[600]">
                                            Client
                                        </label>
                                        <NewDropdown
                                            name={"clientId"}
                                            dataList={clientOptions}
                                            value={watchedClientId}
                                            placeholder={'Select Client'}
                                            onChange={(e) => {
                                                setValue("clientId", e.value);
                                                if (e.value) {
                                                    trigger("clientId");
                                                }
                                            }}
                                        />
                                    </div>
                                )}

                                {/* Button Text */}
                                <div className="flex flex-col shrink-0 basis-0 w-full">
                                    <label className="gap-1 self-stretch text-[14px] w-full text-gray-900 font-[600] capitalize">
                                        Button Text
                                    </label>
                                    <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                        <input
                                            type="text"
                                            placeholder="Enter Button Text"
                                            {...register("buttonText")}
                                            className={`flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-2.5 rounded-md text-base ${errors.buttonText ? "border-red-500" : ""}`}
                                        />
                                    </div>
                                    {errors.buttonText && <p className="text-red text-sm">{errors.buttonText.message}</p>}
                                </div>

                                {/* Link */}
                                <div className="flex flex-col shrink-0 basis-0 w-full">
                                    <label className="gap-1 self-stretch text-[14px] w-full text-gray-900 font-[600] capitalize">
                                        Link
                                    </label>
                                    <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                        <input
                                            type="url"
                                            placeholder="Enter Link URL"
                                            {...register("link")}
                                            className={`flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-2.5 rounded-md text-base ${errors.link ? "border-red-500" : ""}`}
                                        />
                                    </div>
                                    {errors.link && <p className="text-red text-sm">{errors.link.message}</p>}
                                </div>
                            </div>

                            {/* Text - Full Width */}
                            <div className="flex flex-col shrink-0 basis-0 w-full mt-6">
                                <label className="gap-1 self-stretch text-[14px] w-full text-gray-900 font-[600] capitalize">
                                    Advertisement Text
                                </label>
                                <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                    <textarea
                                        placeholder="Enter Advertisement Text"
                                        {...register("adText")}
                                        className={`flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-2.5 rounded-md text-base min-h-[100px] resize-y ${errors.adText ? "border-red-500" : ""}`}
                                    />
                                </div>
                                {errors.adText && <p className="text-red text-sm">{errors.adText.message}</p>}
                            </div>
                        </div>

                        {/* Media Upload Section */}
                        <div className="flex-shrink-0 flex flex-col gap-4">
                            <MediaUpload
                                key={adImage || "ad-new"}
                                clearMedia={mediaClear}
                                label={"Ad Image"}
                                existingImage={adImage}
                                setImageUpload={setAdImage}
                                stateImage={adImage}
                                setStateImageUpload={setAdImage}
                            />
                            <MediaUpload
                                key={image || "img-new"}
                                clearMedia={mediaClear}
                                label={"Image"}
                                existingImage={image}
                                setImageUpload={setImage}
                                stateImage={image}
                                setStateImageUpload={setImage}
                            />
                        </div>
                    </div>

                    {/* Form Actions */}
                    <div className="mt-10 flex gap-5 items-center justify-between">
                        <ActionButton
                            type="button"
                            variant="reset"
                            onClick={() => {
                                reset({
                                    name: '',
                                    type: 'Client',
                                    adText: '',
                                    buttonText: '',
                                    link: '',
                                    brandId: '',
                                    clientId: '',
                                    renderFormat: 'image',
                                });
                                setAdImage(null);
                                setImage(null);
                                setMediaClear(true);
                                setActive(true);
                            }}
                            text="Reset"
                            disabled={isSubmitting}
                        />
                        <div className="flex items-center gap-3">
                            <ActionButton
                                variant="cancel"
                                text="Cancel"
                                handleSubmit={() => navigate('/advertisements/ads-management/list-ads')}
                            />
                            <ActionButton
                                type="submit"
                                variant="primary"
                                text={adId ? 'Update Advertisement' : 'Create Advertisement'}
                                disabled={isCreating || isUpdating}
                            />
                        </div>
                    </div>
                </section>
            </form>
            <ToastContainer
                position="top-right"
            />
        </div>
    );
}

export default CreateAd;
